import { Link } from "react-router-dom";
import { MapPin, Navigation, Star, GlassWater, UtensilsCrossed } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { SaveButton, CrawlButton } from "@/components/VenueActions";
import { fmtWindow, todayKey, money, isFeatured, directionsUrl, DAY_LABELS } from "@/lib/hh";

function Specials({ specials, limit }) {
  const list = limit ? specials.slice(0, limit) : specials;
  return (
    <ul className="space-y-1.5">
      {list.map((s, i) => (
        <li key={i} className="flex items-baseline justify-between gap-3 text-sm">
          <span className="flex items-center gap-2 text-zinc-300">
            {s.category === "food" ? <UtensilsCrossed size={13} className="text-orange-300" /> : <GlassWater size={13} className="text-[#fde68a]" />}
            {s.item}
          </span>
          <span className="shrink-0 font-bold tabular-nums text-[#fde68a]">{money(s.price)}</span>
        </li>
      ))}
      {limit && specials.length > limit && <li className="text-xs text-zinc-500">+{specials.length - limit} more specials</li>}
    </ul>
  );
}

export function FeaturedBadge() {
  return (
    <span data-testid="featured-badge" className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-wider text-zinc-950">
      <Star size={11} fill="currentColor" /> Featured
    </span>
  );
}

export function VenueCard({ venue, day = todayKey(), className = "" }) {
  const featured = isFeatured(venue);
  const isToday = day === todayKey();
  return (
    <article
      data-testid={`venue-card-${venue.id}`}
      className={`fade-up flex flex-col rounded-2xl border p-5 transition-colors hover:border-zinc-600 ${featured ? "border-amber-500/60 bg-gradient-to-b from-amber-500/10 to-[#141419]" : "border-zinc-800 bg-[#141419]"} ${className}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {featured && <FeaturedBadge />}
            {isToday ? <StatusBadge venue={venue} testId={`status-${venue.id}`} /> : <span className="text-xs font-semibold text-zinc-400">{DAY_LABELS[day]}</span>}
          </div>
          <Link to={`/venue/${venue.id}`} data-testid={`venue-link-${venue.id}`} className="font-heading block text-3xl leading-none text-white hover:text-[#fde68a]">
            {venue.name}
          </Link>
          <p className="mt-1.5 flex items-start gap-1.5 text-sm text-zinc-400">
            <MapPin size={14} className="mt-0.5 shrink-0" />
            <span className="line-clamp-2">{venue.address}</span>
          </p>
        </div>
        <SaveButton venue={venue} className="shrink-0" />
      </div>
      <div className="mt-4 flex items-center justify-between border-y border-dashed border-zinc-800 py-2.5 text-sm">
        <span className="font-semibold text-zinc-200">{fmtWindow(venue.hours?.[day])}</span>
        {venue.distance != null && <span data-testid={`distance-${venue.id}`} className="font-bold text-zinc-300">{venue.distance} mi</span>}
      </div>
      <div className="mt-3 flex-1">
        <Specials specials={venue.specials || []} limit={4} />
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <a href={directionsUrl(venue)} target="_blank" rel="noopener noreferrer" data-testid={`directions-${venue.id}`} className="flex items-center gap-1.5 rounded-full bg-[#fde68a] px-3.5 py-2 text-xs font-bold text-zinc-950 hover:bg-yellow-200">
          <Navigation size={13} /> Directions
        </a>
        <CrawlButton venue={venue} />
        <Link to={`/venue/${venue.id}`} className="ml-auto text-xs font-semibold text-zinc-400 hover:text-[#fde68a]">Details →</Link>
      </div>
    </article>
  );
}

export { Specials };
